import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import config from '@config/index';
import axios, { isAxiosError, AxiosHeaders, type AxiosResponse } from 'axios';
import type { IssueRequest } from '@shared/types';
import { apiRequestCache, apiCacheKey, apiCacheGeneration, clearApiRequestCache } from './api-cache';
import { gameRequestHeaders } from './game-request';

export const API_BASE = '/api';
axios.defaults.baseURL = getBaseUrl();
interface ApiError { message: string; }
type HandleErrorFn = (message: string, statusCode: number | undefined) => void;
const defaultErrorHandler: HandleErrorFn = (message, status) => {
  if ((status && status >= 500) || !status) toast.error(message);
};

const useAxios = <Response>(handleError: HandleErrorFn = defaultErrorHandler) => {
  const { t } = useTranslation();
  const [response, setResponse] = useState<Response>();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusCode, setStatusCode] = useState<number>();
  const latestRequest = useRef(0);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const issueRequest: IssueRequest = useCallback(async ({ url, method, body, headers, query, responseType }) => {
    const requestId = ++latestRequest.current;
    const current = () => mounted.current && latestRequest.current === requestId;
    setLoading(true);
    setError('');
    const normalizedMethod = method.toLowerCase();
    const baseURL = getBaseUrl();
    const requestHeaders = new AxiosHeaders({ ...gameRequestHeaders(url, body), ...headers });
    if (body instanceof FormData) requestHeaders.delete('Content-Type');
    if (normalizedMethod !== 'get') clearApiRequestCache();
    const generation = apiCacheGeneration();
    const cacheKey = apiCacheKey(baseURL, url, query, requestHeaders.toJSON(), responseType);

    const request = async (): Promise<AxiosResponse<Response>> => {
      for (let attempt = 0; ; attempt++) {
        try {
          return await axios<Response>({ method, baseURL, url, data: body, headers: requestHeaders, params: query,
            responseType, withCredentials: true, timeout: body instanceof FormData ? 120000 : 30000 });
        } catch (failure) {
          if (!isAxiosError(failure) || failure.response?.status !== 429 || attempt >= 3) throw failure;
          const retryAfter = Number(failure.response.headers?.['retry-after']);
          // Do not schedule minutes of hidden retries; let the UI display rate limiting instead.
          if (Number.isFinite(retryAfter) && retryAfter > 30) throw failure;
          await new Promise(resolve => setTimeout(resolve, retryAfter > 0 ? retryAfter * 1000 : 2 ** attempt * 1000));
        }
      }
    };

    try {
      let promise: Promise<AxiosResponse>;
      const cached = normalizedMethod === 'get' ? apiRequestCache.get(cacheKey) : undefined;
      if (cached && cached.generation === generation && cached.expiresAt > Date.now()) {
        promise = cached.promise;
      } else {
        promise = request();
        if (normalizedMethod === 'get') {
          const entry = { promise, expiresAt: Number.POSITIVE_INFINITY, generation };
          apiRequestCache.set(cacheKey, entry);
          // Both handlers consume settlement; rejected cached requests never create an unhandled promise.
          void promise.then(() => {
            if (apiRequestCache.get(cacheKey) === entry) {
              if (apiCacheGeneration() !== generation) apiRequestCache.delete(cacheKey);
              else entry.expiresAt = Date.now() + 2000;
            }
          }, () => { if (apiRequestCache.get(cacheKey) === entry) apiRequestCache.delete(cacheKey); });
          // Bound cache growth without timers that can delete a newer request's entry.
          if (apiRequestCache.size > 200) {
            for (const [key, entry] of apiRequestCache) if (entry.expiresAt < Date.now()) apiRequestCache.delete(key);
          }
        }
      }
      const result = await promise;
      if (current()) {
        setStatusCode(result.status);
        setResponse(result.data as Response);
      }
    } catch (failure) {
      if (!current()) return;
      let status: number | undefined;
      let message = t('api.unknownError');
      if (isAxiosError<ApiError>(failure)) {
        status = failure.response?.status;
        setStatusCode(status);
        if (!failure.response) message = t('api.networkError');
        else {
          const messages: Record<number, string> = { 429: 'api.tooManyRequests', 401: 'api.sessionExpired',
            403: 'api.accessDenied', 404: 'api.resourceNotFound', 500: 'api.serverError' };
          message = messages[status!] ? t(messages[status!]) : typeof failure.response.data === 'string'
            ? failure.response.data : failure.response.data?.message || t('api.requestError');
          if (status === 401) {
            const skipRedirect = url.includes('/auth/user') || url.includes('/public/') || requestHeaders.get('x-skip-auth-redirect');
            if (skipRedirect) message = '';
            else {
              clearApiRequestCache();
              try { localStorage.removeItem('authData'); } catch { /* Restricted browser storage. */ }
              if (!window.location.pathname.includes('/login')) {
                setTimeout(() => {
                  window.location.href = window.location.pathname.startsWith('/admin') ? '/admin/login'
                    : window.location.pathname.startsWith('/partners') ? '/partners/login' : '/login';
                }, 1000);
              }
            }
          }
        }
      }
      if (message) handleError(message, status);
      setError(message);
    } finally {
      if (current()) setLoading(false);
      if (normalizedMethod !== 'get') clearApiRequestCache();
    }
  }, [handleError, t]);

  return { response, error, issueRequest, loading, statusCode, setLoading };
};

export function getBaseUrl() {
  const { hostname, protocol, port } = window.location;
  
  // Проверяем, локальный ли запуск или IP-адрес
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const isLocalIP = /^192\.168\.|^10\.|^172\.(1[6-9]|2[0-9]|3[0-1])\.|^127\./.test(hostname);

  // В режиме разработки
  if (import.meta.env.DEV) {
    // Если мы на localhost, используем localhost для API
    if (isLocalhost) {
      console.log('🔍 Using localhost for API in dev mode');
      return `${protocol}//localhost:${config.serverPort}/api`;
    }
    
    // Если мы на локальном IP (например, с телефона), используем IP компьютера
    if (isLocalIP) {
      // Определяем IP компьютера из URL
      const computerIP = hostname;
      console.log('🔍 Using computer IP for API in dev mode:', computerIP);
      return `${protocol}//${computerIP}:${config.serverPort}/api`;
    }
  }

  // В продакшене используем текущий hostname
  let baseUrl = `${protocol}//${hostname}`;
  if (port && port !== '80' && port !== '443') {
    baseUrl += `:${port}`;
  }
  baseUrl += '/api';
  return baseUrl;
}



export default useAxios;
